import { useEffect, useState } from "react";
import { HeartIcon, MenuDotsIcon } from "../../util/Icons";
import ProfilePicture from "../../util/ProfilePicture";
import { timeAgo, WhoLiked } from "./SocialFeed";
import { collection, doc, getDoc, increment, runTransaction } from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserContext } from "../../../context/UserProvider";
import { Link } from "react-router-dom";
import DisplayName from "../../util/DisplayName";

export default function SingleComment({ commentData, onClick = () => { }, editComment }) {
    const { user, userDetails } = useUserContext()
    const [hasLiked, setHasLiked] = useState(false)
    const [likeButtonEnabled, setLikeButtonEnabled] = useState(true)
    const [showMenu, setShowMenu] = useState(false)
    const [isEditingComment, setIsEditingComment] = useState(false)
    const [editMessage, setEditMessage] = useState(commentData?.message || "")
    const [editError, setEditError] = useState("")
    const [isSavingEdit, setIsSavingEdit] = useState(false)
    const comment = commentData
    const postId = comment?.postId || comment?.ogPostID
    const commentAuthorId = comment?.commentAuthorID || comment?.authorId
    const canEditComment = user.uid === commentAuthorId && typeof editComment === "function"


    const postDocRef = doc(db, "social_feed", postId)
    const commentDocRef = doc(db, "social_feed", postId, "comments", comment.id)
    const likesCollectionRef = collection(db, "social_feed", postId, "comments", comment.id, "likes")
    const likeDocRef = doc(db, "social_feed", postId, "comments", comment.id, "likes", user.uid)
    useEffect(() => {
        async function getHasLiked() {
            const likeDoc = await getDoc(likeDocRef)
            setHasLiked(likeDoc.exists())
        }
        getHasLiked()
    }, [])
    useEffect(() => {
        if (!isEditingComment) {
            setEditMessage(comment?.message || "")
        }
    }, [comment?.message, isEditingComment])
    async function handleLike() {
        if (!likeButtonEnabled) return;
        setLikeButtonEnabled(false)
        // console.log("handling comment like")
        let newLikeState = hasLiked
        //Check if has liked
        try {
            await runTransaction(db, async (transaction) => {
                const postDoc = await transaction.get(postDocRef)
                if (!postDoc.exists()) {
                    throw new Error("Can't like, post doesn't exist!! postId ", postId)
                }
                const commentDoc = await transaction.get(commentDocRef)
                if (!commentDoc.exists()) {
                    throw new Error("Can't like, comment doesn't exist, postId ", postId, " comment id ", comment.id)
                }
                const likeDoc = await transaction.get(likeDocRef)
                if (likeDoc.exists()) {
                    //remove Like
                    // console.log("remove like")
                    transaction.delete(likeDocRef)
                    transaction.update(commentDocRef, { count_likes: increment(-1) })
                    comment.count_likes = comment.count_likes > 0 ? comment.count_likes - 1 : 0
                    newLikeState = false
                } else {
                    //add like
                    // console.log("add like")
                    transaction.set(likeDocRef, {
                        uid: user.uid,
                        authorId: user.uid,
                        userName: userDetails?.firstName + " " + userDetails?.lastName,
                        createdAt: Date.now(),
                        epochTime: Date.now()
                    })
                    transaction.update(commentDocRef, { count_likes: increment(1) })
                    comment.count_likes += 1
                    newLikeState = true
                }
            })
        } catch (err) {
            console.error(err.message)
        }

        setHasLiked(newLikeState)
        await new Promise((resolve) => {
            setTimeout(() => resolve(), 2000)
        })
        setLikeButtonEnabled(true)
        // console.log("handle like end")
    }
    function startEditingComment() {
        setEditMessage(comment?.message || "")
        setEditError("")
        setIsEditingComment(true)
    }
    function cancelEditingComment() {
        setEditMessage(comment?.message || "")
        setEditError("")
        setIsEditingComment(false)
    }
    async function saveCommentEdit() {
        if (isSavingEdit || typeof editComment !== "function") return
        const nextMessage = editMessage.trim()
        if (nextMessage.length < 1) {
            setEditError("Comment must be at least 1 character")
            return
        }
        if (nextMessage === (comment?.message || "").trim()) {
            setIsEditingComment(false)
            return
        }
        setIsSavingEdit(true)
        setEditError("")
        try {
            await editComment(comment, nextMessage)
            setIsEditingComment(false)
        } catch (err) {
            console.error("Could not edit comment:", err.message)
            setEditError("Failed to save comment. Please try again.")
        }
        setIsSavingEdit(false)
    }
    return (
        <div className="flex gap-2 mb-2">
            <Link className="no-underline" to={`/profile/${commentAuthorId}`}>
                <ProfilePicture
                    userID={commentAuthorId}
                    name={comment.authorName}
                    className="text-black bg-white flex-shrink-0"
                ></ProfilePicture>
            </Link>
            <div className="flex flex-col max-w-[85%]">
                <div className="px-3 py-2 bg-slate-100 rounded-2xl">
                    {comment.replyTo?.commentAuthorID && (
                        <div className="mb-2 pl-2 border-l-2 border-slate-300">
                            <p className="m-0 text-xs text-slate-500">
                                {/* <span className="font-bold text-slate-600">Replying to:</span> {comment.replyTo?.authorName} */}
                                <span className="font-bold text-slate-600">{comment.replyTo?.authorName} said: </span> 
                            </p>
                            <p className="m-0 text-xs text-slate-400 italic">
                                "{comment.replyTo?.message.slice(0, 20) + (comment.replyTo?.message.length > 20 ? "..." : "")}"
                            </p>
                        </div>
                    )}
                    <div className="flex items-start gap-2">
                        <Link className="no-underline text-current min-w-0 flex-1" to={`/profile/${commentAuthorId}`}>
                            {/* <p className="m-0 font-medium text-sm text-slate-900">{comment?.authorName}</p> */}
                            <DisplayName uid={commentAuthorId} fullName={comment.authorName} className="text-sm leading-tight break-words" />
                        </Link>
                        {canEditComment && (
                            <div className="relative flex-shrink-0 -mr-1 -mt-1">
                                <button
                                    type="button"
                                    className="p-1 rounded-full hover:bg-slate-200 transition-colors text-slate-500"
                                    onClick={() => setShowMenu(!showMenu)}
                                >
                                    <MenuDotsIcon size={18} />
                                </button>
                                {showMenu && (
                                    <>
                                        <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)}></div>
                                        <div className="absolute right-0 mt-1 w-28 bg-white rounded-lg shadow-lg border border-slate-100 py-1 z-20 overflow-hidden">
                                            <button
                                                type="button"
                                                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors font-medium"
                                                onClick={() => {
                                                    startEditingComment()
                                                    setShowMenu(false)
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                    {isEditingComment ? (
                        <div className="mt-1">
                            <textarea
                                value={editMessage}
                                onChange={(e) => {
                                    setEditMessage(e.target.value)
                                    setEditError("")
                                }}
                                rows={2}
                                className="border border-slate-300 rounded-lg px-3 py-2 w-full bg-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all text-sm resize-y"
                                disabled={isSavingEdit}
                            />
                            {editError && <p className="text-red-500 text-xs mt-1 mb-0">{editError}</p>}
                            <div className="flex justify-end gap-2 mt-2">
                                <button
                                    type="button"
                                    className="px-3 py-1 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-medium transition-colors disabled:opacity-50"
                                    onClick={cancelEditingComment}
                                    disabled={isSavingEdit}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="px-3 py-1 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={saveCommentEdit}
                                    disabled={isSavingEdit}
                                >
                                    {isSavingEdit ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="m-0 text-slate-800 text-sm whitespace-pre-line">{comment?.message}</p>
                    )}
                </div>
                <div className="flex gap-x-3 ml-3 mt-1 items-center flex-wrap">
                    <p className="m-0 font-light text-[10px] text-slate-500">{timeAgo(comment?.epochTime)}</p>
                    {(comment?.editedAt || comment?.updatedAt) && <p className="m-0 font-light text-[10px] text-slate-400">Edited</p>}
                    <button type="button" className="like-button text-sm flex items-center transition-transform enabled:active:scale-90 gap-1 enabled:cursor-pointer"
                        onClick={(e) => {
                            let parent = e.target.closest("button.like-button");
                            if (!hasLiked) {
                                parent.classList.add("heart-animate");
                            }
                            handleLike()
                        }}
                        style={{ color: hasLiked ? "#ef4444" : "currentcolor" }}
                        onAnimationEnd={(e) => e.target.classList.remove("heart-animate")}
                        disabled={!likeButtonEnabled}>
                        <HeartIcon size={22} style={hasLiked ? "fill" : "stroke"} color={"currentcolor"} />
                        <span>
                            {comment?.count_likes}
                        </span>

                    </button>
                    <WhoLiked count={comment?.count_likes} likeCollection={likesCollectionRef} />
                    <button type="button" className="m-0 font-bold text-xs text-slate-600 hover:text-slate-800" onClick={onClick}>
                        Reply
                    </button>
                </div>
            </div>
        </div>
    );
}
